import { useState, useEffect, type ReactNode } from 'react';
import ReactDOM from 'react-dom';
import { ToastPortal } from '~/components';
import { type Toast, ToastContext, type ToastContextValue } from '~/context';
import { type ToastPosition } from '~/types';

interface ToastProviderProps {
  children: ReactNode;
  defaultPosition?: ToastPosition;
}

export function ToastProvider({
  children,
  defaultPosition = 'top-center',
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const div = document.createElement('div');
    document.body.appendChild(div);
    setContainer(div);
    return () => {
      document.body.removeChild(div);
    };
  }, []);

  const addToast = (toast: Omit<Toast, 'id'>) => {
    setToasts((prev) => [
      ...prev,
      {
        autoClose: 5000,
        ...toast,
        id: Math.random().toString(),
        position: toast.position || defaultPosition,
      },
    ]);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const value: ToastContextValue = {
    addToast,
    removeToast,
    defaultPosition,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {container &&
        ReactDOM.createPortal(
          <ToastPortal toasts={toasts} removeToast={removeToast} />,
          container,
        )}
    </ToastContext.Provider>
  );
}
